import { computeQualityScore } from "@/lib/quality/score";
import type { ProcessingResult } from "@/types";

export interface SnapshotStats {
  quality_score: number;
  avg_margin_percent: number;
  total_price_value: number;
  categories: Record<string, number>;
}

/**
 * Derive the summary stats stored on a snapshot from a processing result.
 *
 * `quality_score` is a simple, transparent placeholder — the share of SKUs with
 * no quality issue, 0–100 — until the configurable, weighted Quality Score
 * feature lands in a later v0.2 prompt.
 */
export function computeSnapshotStats(result: ProcessingResult): SnapshotStats {
  const { rows } = result;
  const categories: Record<string, number> = {};
  let marginSum = 0;
  let marginCount = 0;
  let priceTotal = 0;

  for (const row of rows) {
    categories[row.category] = (categories[row.category] ?? 0) + 1;
    if (row.margin_percent !== null) {
      const margin = Number(row.margin_percent);
      if (Number.isFinite(margin)) {
        marginSum += margin;
        marginCount += 1;
      }
    }
    const price = Number(row.price);
    if (Number.isFinite(price)) priceTotal += price;
  }

  const quality_score = computeQualityScore(result.issues, rows.length);

  return {
    quality_score,
    avg_margin_percent: marginCount === 0 ? 0 : Number((marginSum / marginCount).toFixed(2)),
    total_price_value: Number(priceTotal.toFixed(2)),
    categories,
  };
}
